/**
 * Eligibility-result staleness for the Daily Cockpit.
 *
 * A definitive coverage answer is stale once the visit it was validated for has
 * passed — re-verify before relying on it for an upcoming appointment.
 *
 * The date of service we judge against is resolved with a three-branch fallback,
 * because not every check records the visit it was run for:
 *   1. the check's own `appointmentDate` — set by the sweep for new Stedi/RPA
 *      checks; the true DOS;
 *   2. else, if the displayed check IS this appointment's own linked check
 *      (`check.id === appointment.eligibilityCheckId`), the appointment's own
 *      date — covers legacy swept checks created before per-check appointmentDate
 *      persistence; the DOS is recoverable from the row;
 *   3. else, when the check ran (`checkedAt`) — a manual one-off, or a prior
 *      visit's set-check that isn't this appointment's; checkedAt approximates
 *      its relevance window well enough.
 *
 * `checkedAt` is a last resort (branch 3) only — never the primary signal — so a
 * normal swept check run days ahead of an upcoming visit doesn't read stale.
 */

/**
 * True once `dateStr`'s calendar day has fully elapsed relative to `now`.
 *
 * The boundary is **local** midnight, not UTC. A date of service is a calendar
 * day *at the clinic*, and front-desk staff use this app on-site — so the
 * browser's timezone is the clinic's. This also matches how the Cockpit buckets
 * a day everywhere else (`services/appointments.parseIsoDateStr` parses ISO
 * dates at local midnight too, as does the horizon). Judging against UTC
 * midnight (a trailing `Z`) instead flips a same-day result to "stale" in the
 * clinic's afternoon/evening — ~5pm PT / 8pm ET — while the visit is still
 * today. Do not reintroduce the `Z`. (#83)
 */
function dayHasPassed(dateStr: string | null | undefined, now: number): boolean {
  if (!dateStr) return false
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return false
  // Start of the *next* calendar day at local midnight — the DOS day stays
  // fresh through its own local end. `new Date(y, mIdx, d + 1)` rolls month/year
  // over correctly and respects DST-length days (unlike adding a fixed 24h).
  const nextDayStartMs = new Date(year, month - 1, day + 1).getTime()
  if (Number.isNaN(nextDayStartMs)) return false
  return nextDayStartMs <= now
}

export interface DosResolutionInputs {
  /** DOS recorded on the check itself — set by the sweep; absent on legacy/manual checks. */
  checkAppointmentDate?: string | null
  /** When the check ran (ISO) — last-resort proxy. */
  checkedAt?: string | null
  /** The displayed check's id. */
  checkId?: string | null
  /** The appointment row's own linked check id (set by the sweep when it checks the appt). */
  appointmentEligibilityCheckId?: string | null
  /** The appointment row's date of service (ISO). */
  appointmentDate?: string | null
}

/**
 * Resolve the date of service to judge a Cockpit check's staleness against.
 * See the module header for the three-branch rationale.
 */
export function resolveDateOfService(inputs: DosResolutionInputs): string | null | undefined {
  const {
    checkAppointmentDate, checkedAt, checkId,
    appointmentEligibilityCheckId, appointmentDate,
  } = inputs
  // 1. The check carries its own DOS.
  if (checkAppointmentDate) return checkAppointmentDate
  // 2. The displayed check is this appointment's own check → use the row's DOS.
  if (checkId && appointmentEligibilityCheckId && checkId === appointmentEligibilityCheckId) {
    return appointmentDate
  }
  // 3. Not this visit's check and no recorded DOS → fall back to when it ran.
  return checkedAt
}

/**
 * True when the check's (resolved) date of service has fully passed — i.e. the
 * result is stale and the patient should be re-verified for the upcoming visit.
 * `now` is epoch ms (injectable for tests). Returns false when the resolved date
 * is missing or unparseable: we can't judge staleness, so we don't flag it.
 */
export function isCheckStale(inputs: DosResolutionInputs, now: number): boolean {
  return dayHasPassed(resolveDateOfService(inputs), now)
}
